import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import {
	TonConnectButton,
	useTonAddress,
	useTonConnectUI,
	useTonWallet,
} from '@tonconnect/ui-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { GiCheckMark } from 'react-icons/gi';
import { useCustomDispatch } from '@/core/redux/hooks';
////////////////////////////////////////////////////////////////////
import {
	BackButton,
	Button,
	PageTitle,
	TokenList,
} from '@/app/shared/components';

import {
	setCounter,
	setDiamonds,
	setStickerWonDialog,
	setTonWallet,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
import { WalletService } from '@/core/services';
import { UpgradeActivityResponse } from '@/core/interfaces';
import { TonService } from '@/core/services';

/**
 * Wallet page component.
 *
 * This component is responsible for rendering the wallet page of the
 * application. It displays a welcome message and a button to connect to a
 * TON wallet. If a wallet is connected, it displays the balances of the
 * connected wallet.
 *
 * @returns {JSX.Element} The rendered component.
 */
const Wallet: React.FC = (): JSX.Element => {
	/*********************
	// #region Constants
	**********************/
	const shuriJettonMasterAddress =
		'kQBauG6xOOq-uIMggPKPHEbRBRn-HeD4uVF8-7TGdm016O56';
	const usdtJettonMasterAddress =
		'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';

	/*********************
	// #region Hooks
	**********************/
	const userFriendlyAddress = useTonAddress();
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();
	const wallet = useTonWallet();
	const [tonConnectUI] = useTonConnectUI();
	const [triggerWalletModal, setTriggerWalletModal] = useState(true);
	const [tonBalance, setTonBalance] = useState('');
	const [shuriBalance, setShuriBalance] = useState('');
	const [userShuriJettonWalletAddr, setUserShuriJettonWalletAddr] =
		useState('');
	const [usdtBalance, setUSDTBalanace] = useState('');
	const [userUsdtJettonWalletAddr, setUserUsdtJettonWalletAddr] =
		useState('');

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/wallet',
			title: 'Wallet',
		});
	}, []);

	useEffect(() => {
		if (tonConnectUI.connected) return;
		setTonBalance('0');
		setShuriBalance('0');
		setUSDTBalanace('0');
		setUserShuriJettonWalletAddr('');
		setUserUsdtJettonWalletAddr('');
	}, [tonConnectUI.connected]);

	useEffect(() => {
		if (!wallet) return;
		if (!tonConnectUI.connected) return;

		const getBalance = async () => {
			try {
				const response = await TonService.getNativeBalance(
					wallet.account.address
				);
				setTonBalance(response.balance);
			} catch (error) {
				setTonBalance('0');
				console.error('Error on get ton balance', error);
			}
		};
		getBalance();
	}, [tonConnectUI.connected]);

	//get shuri jetton wallet address
	useEffect(() => {
		if (!wallet) return;
		if (!tonConnectUI.connected) return;
		if (tonBalance === '') return;

		getJettonWalletAddress(
			shuriJettonMasterAddress,
			wallet.account.address,
			(jettonWalletAddress) => {
				setUserShuriJettonWalletAddr(jettonWalletAddress);
			},
			() => {
				setUserShuriJettonWalletAddr('');
				setShuriBalance('0');
			},
			'Error on get shuri jetton wallet address'
		);
	}, [tonBalance, wallet, tonConnectUI.connected]);

	useEffect(() => {
		if (!wallet) return;
		if (!tonConnectUI.connected) return;
		if (userShuriJettonWalletAddr === '') return;

		getJettonWalletBalance(
			userShuriJettonWalletAddr,
			(balance) => {
				setShuriBalance(balance);
			},
			() => {
				setShuriBalance('0');
			},
			'Error on get shuri jetton wallet balance'
		);
	}, [userShuriJettonWalletAddr, tonConnectUI.connected]);

	//get usdt jetton wallet address
	useEffect(() => {
		if (!wallet) return;
		if (!tonConnectUI.connected) return;
		if (shuriBalance === '') return;

		getJettonWalletAddress(
			usdtJettonMasterAddress,
			wallet.account.address,
			(jettonWalletAddress) => {
				setUserUsdtJettonWalletAddr(jettonWalletAddress);
			},
			() => {
				setUserUsdtJettonWalletAddr('');
				setUSDTBalanace('0');
			},
			'Error on get usdt jetton wallet address'
		);
	}, [shuriBalance, wallet, tonConnectUI.connected]);

	useEffect(() => {
		if (!wallet) return;
		if (!tonConnectUI.connected) return;
		if (userUsdtJettonWalletAddr === '') return;

		getJettonWalletBalance(
			userUsdtJettonWalletAddr,
			(balance) => {
				setUSDTBalanace(balance);
			},
			() => {
				setUSDTBalanace('0');
			},
			'Error on get usdt jetton wallet balance'
		);
	}, [userUsdtJettonWalletAddr, tonConnectUI.connected]);

	useEffect(() => {
		if (!userFriendlyAddress) return;
		if (!wallet) return;
		if (triggerWalletModal) return;

		toast.promise(
			WalletService.setTonWallet({
				account: userFriendlyAddress,
				provider: wallet.device.appName,
			}),
			{
				loading: t('general.loading'),
				success: (data: UpgradeActivityResponse) => {
					// Emit event to Google Analytics
					ReactGA.event({
						category: 'wallet',
						action: 'Connect TON Wallet',
					});
					// Update counter
					dispatch(
						setTonWallet({
							account: userFriendlyAddress,
							provider: wallet.device.appName,
						})
					);

					dispatch(setCounter(data.currentCoins));
					dispatch(setDiamonds(data.currentDiamonds));
					dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));

					if (data.isAwardWinning) {
						dispatch(setStickerWonDialog(true));
					}

					return t('wallet.connected');
				},
				error: t('wallet.connectError'),
			}
		);
	}, [userFriendlyAddress, wallet]);

	/*********************
	// #region Functions
	**********************/
	const getJettonWalletAddress = async (
		jettonMasterAddress: string,
		walletAddress: string,
		onSuccess: (jettonWalletAddress: string) => void,
		onError: () => void,
		errorMessage: string
	) => {
		//TODO get jetton master address from back
		try {
			const response = await TonService.getJettonWalletAddress(
				jettonMasterAddress,
				walletAddress
			);
			onSuccess(response.jettonWalletAddress);
		} catch (error) {
			onError();
			console.error(errorMessage, error);
		}
	};

	const getJettonWalletBalance = async (
		jettonWalletAddress: string,
		onSuccess: (jettonBalance: string) => void,
		onError: () => void,
		errorMessage: string
	) => {
		try {
			const response = await TonService.getJettonBalance(
				jettonWalletAddress
			);
			onSuccess(response.jettonBalance);
		} catch (error) {
			onError();

			console.error(errorMessage, error);
		}
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-4 pt-8 pb-32 mx-4">
			<BackButton />
			<div className="flex flex-col items-center">
				<PageTitle>Wallet</PageTitle>
				{!wallet && (
					<p className="text-sm text-center">{t('wallet.intro')}</p>
				)}
				{wallet && (
					<div className="mt-2">
						<TonConnectButton />
					</div>
				)}
			</div>

			{!wallet ? (
				<div className="my-6">
					<div className="mb-6 px-6 flex justify-center">
						<img
							className="max-w-[170px]"
							src={process.env.VITE_WALLET_PAGE_IMAGE}
							alt={t('wallet.intro')}
						/>
					</div>
					<ul className="mx-4 mt-3 flex flex-col gap-3">
						<li className="flex items-center gap-3">
							<div className="bg-gradient-to-br from-green-400 via-green-500 to-green-800 rounded-full p-1">
								<GiCheckMark className="text-white" />
							</div>
							{t('wallet.options.one')}
						</li>
						<li className="flex items-center gap-3">
							<div className="bg-gradient-to-br from-green-400 via-green-500 to-green-800 rounded-full p-1">
								<GiCheckMark className="text-white" />
							</div>
							{t('wallet.options.two')}
						</li>
						<li className="flex items-center gap-3">
							<div className="bg-gradient-to-br from-green-400 via-green-500 to-green-800 rounded-full p-1">
								<GiCheckMark className="text-white" />
							</div>
							{t('wallet.options.three')}
						</li>
						<li className="flex items-center gap-3">
							<div className="bg-gradient-to-br from-green-400 via-green-500 to-green-800 rounded-full p-1">
								<GiCheckMark className="text-white" />
							</div>
							{t('wallet.options.four')}
						</li>
						<li className="flex items-center gap-3">
							<div className="bg-gradient-to-br from-green-400 via-green-500 to-green-800 rounded-full p-1">
								<GiCheckMark className="text-white" />
							</div>
							{t('wallet.options.five')}
						</li>
					</ul>
				</div>
			) : (
				<div className="my-6">
					<div className="mb-6 px-6 flex justify-center">
						<img
							className="max-w-[170px]"
							src={process.env.VITE_WALLET_COINS_IMAGE}
							alt={'Ton Coins'}
						/>
					</div>
					<TokenList
						tonBalance={tonBalance}
						shuriBalance={shuriBalance}
						usdtBalance={usdtBalance}
					/>
				</div>
			)}
			{!wallet && (
				<Button
					onClick={() => {
						tonConnectUI.openModal();
						setTriggerWalletModal(false);
					}}
				>
					{t('wallet.connectTonWallet')}
				</Button>
			)}
		</div>
	);
};

export default Wallet;
